/** Игровой стол: крупный график слева; этап, банк, места, прогноз и действия справа. */
import type { GameView, RoomView } from '@mt/protocol';
import { client, useApp } from '../net/client';
import { clock, cr, pts, secondsLeft, useNow } from '../lib/format';
import { Chart } from './Chart';
import { Controls } from './Controls';
import { GameSeats, RoomSeats } from './Seats';
import { Settlement } from './Settlement';
import { StageTrack } from './StageTrack';
import { WaitingRoom } from './WaitingRoom';

export function Table({ room }: { room: RoomView }) {
  const game = useApp((s) => s.game);
  const chart = useApp((s) => s.chart);
  const profile = useApp((s) => s.profile);
  const now = useNow();
  const showChart = game && chart?.gameId === game.gameId ? chart : null;

  return (
    <div className="table-page">
      <div className="table-head">
        <button className="btn ghost tiny" onClick={() => client.leave()} title={room.meSeated && game && !game.settlement && game.me.participant ? 'Вы останетесь участником до конца розыгрыша' : ''}>
          ← Лобби
        </button>
        <span className="title">
          Стол {room.ante / 100} кр. <span className="dim">· {room.roomId}</span>
        </span>
        {game ? (
          <span className="badges">
            <span className={`badge ${game.mode === 'replay' ? 'warn' : 'live'}`}>{game.mode === 'replay' ? 'Воспроизведение истории' : 'Реальное время'}</span>
            {game.source === 'synthetic' && <span className="badge warn">Синтетические данные</span>}
          </span>
        ) : null}
      </div>

      {!game ? (
        <WaitingRoom room={room} now={now} />
      ) : (
        <div className="table-grid">
          <div className="main-col">
            <StageTrack game={game} now={now} nextStartAt={room.status === 'results' ? room.nextStartAt : null} />
            {game.settlement ? (
              <div className="result-split">
                <Chart chart={showChart} startAt={game.startAt} minuteMs={game.minuteMs} forecast={game.me.forecast} settled />
                <Settlement game={game} s={game.settlement} />
              </div>
            ) : (
              <Chart chart={showChart} startAt={game.startAt} minuteMs={game.minuteMs} forecast={game.me.forecast} settled={false} />
            )}
            {!game.settlement && <InfoStrip game={game} />}
          </div>

          <aside className="side-col">
            {game && !game.settlement && (
              <section className="panel pot">
                <div>
                  <span className="dim">Банк</span>
                  <b className="mono">{cr(game.pot)}</b>
                </div>
                <div>
                  <span className="dim">P₀</span>
                  <b className="mono">{pts(game.p0)}</b>
                </div>
                <div>
                  <span className="dim">Взнос N</span>
                  <b className="mono">{cr(game.ante)}</b>
                </div>
              </section>
            )}
            {/* решения — сразу под банком, чтобы кнопки действий всегда были на виду */}
            {game && !game.settlement && <Controls game={game} now={now} />}
            <ReadyPanel room={room} game={game} balance={profile?.balance ?? 0} now={now} />
            <section className="panel seats-panel">
              <div className="row-title">
                <span>{game && !game.settlement ? 'Участники' : 'За столом'}</span>
                <span className="dim small">
                  {game && !game.settlement ? game.seats.length : room.seats.length}/{room.maxPlayers} · суммы в кр.
                </span>
              </div>
              {game && !game.settlement ? <GameSeats game={game} now={now} /> : <RoomSeats room={room} />}
            </section>
          </aside>
        </div>
      )}
    </div>
  );
}

function InfoStrip({ game }: { game: GameView }) {
  const actions = [...game.actions].reverse();
  return (
    <div className="info-strip">
      <section className="panel feed" aria-label="Ходы участников">
        <div className="row-title">
          <span>Ходы участников</span>
          <span className="dim small">прогнозы скрыты до расчёта</span>
        </div>
        <ul className="feed-list">
          {actions.length === 0 && <li className="dim">Ходов пока нет</li>}
          {actions.map((a) => (
            <li key={a.seq} className={`feed-item ${a.kind} ${a.action ?? ''} ${game.seats.find((s) => s.playerId === a.playerId)?.isMe ? 'me' : ''}`}>
              <span className="mono dim">{clock(a.at)}</span>
              <span className="feed-round">{a.round === 0 ? 'взнос' : `круг ${a.round}`}</span>
              <span className="feed-text">{a.text}</span>
            </li>
          ))}
        </ul>
      </section>
      <section className="panel reveal" aria-label="Раскрытие информации">
        <div className="row-title">
          <span>Раскрытие информации</span>
          <span className="dim small" title={game.commitment.hash}>
            обязательство {game.commitment.hash.slice(0, 12)}…
          </span>
        </div>
        <div className="composition">
          {game.composition ? (
            game.composition.map((c) => (
              <span key={c.symbol} className="ticker">
                <b>{c.symbol}</b> {c.name}
              </span>
            ))
          ) : (
            <span className="dim">Состав скрыт до круга 3</span>
          )}
        </div>
        <ul className="log">
          {[...game.revealLog].reverse().map((e, i) => (
            <li key={i}>
              <span className="mono dim">{clock(e.at)}</span> {e.text}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function ReadyPanel({ room, game, balance, now }: { room: RoomView; game: GameView | null; balance: number; now: number }) {
  const inActive = game && !game.settlement && game.me.participant;
  if (inActive) return null;
  if (!room.meSeated) {
    return (
      <section className="panel">
        <p className="dim">Вы наблюдаете за столом.</p>
      </section>
    );
  }
  const enough = balance >= room.ante;
  return (
    <section className="panel ready-panel">
      {room.meReady ? (
        <>
          <p>
            <b className="ok">Участие подтверждено.</b> Взнос {cr(room.ante)} будет внесён при старте.
          </p>
          <button className="btn ghost" onClick={() => client.ready(false)}>
            Отменить участие
          </button>
        </>
      ) : (
        <>
          <button className="btn primary wide" disabled={!enough} onClick={() => client.ready(true)}>
            Участвовать в {room.status === 'playing' || room.status === 'results' ? 'следующем ' : ''}розыгрыше · взнос {cr(room.ante)}
          </button>
          {!enough && <p className="warn-line">Недостаточно кредитов для взноса.</p>}
        </>
      )}
    </section>
  );
}
